'use client';

// Fournisseur de thème : mode auto/light/dark persisté, appliqué sur <html>.
// En "auto" on RETIRE data-theme et on laisse le CSS `@media prefers-color-scheme`
// gérer. La lecture de la préférence passe par useSyncExternalStore (pas de
// setState dans un effet → aucune cascade ni mismatch d'hydratation : le snapshot
// serveur vaut 'auto', le client relit le vrai choix après hydratation).

import { createContext, useCallback, useContext, useSyncExternalStore, type ReactNode } from 'react';

import { THEME_STORAGE_KEY } from './themeScript';

export type ThemeMode = 'auto' | 'light' | 'dark';

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  window.addEventListener('storage', cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', cb);
  };
}

function readMode(): ThemeMode {
  try {
    const m = localStorage.getItem(THEME_STORAGE_KEY);
    return m === 'light' || m === 'dark' ? m : 'auto';
  } catch {
    return 'auto';
  }
}

function applyMode(mode: ThemeMode): void {
  const el = document.documentElement;
  if (mode === 'light' || mode === 'dark') {
    el.setAttribute('data-theme', mode);
  } else {
    el.removeAttribute('data-theme');
  }
}

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const mode = useSyncExternalStore(subscribe, readMode, (): ThemeMode => 'auto');

  const setMode = useCallback((next: ThemeMode) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // localStorage indisponible : le thème reste appliqué pour la session.
    }
    applyMode(next);
    emit();
  }, []);

  return <ThemeContext.Provider value={{ mode, setMode }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans <ThemeProvider>.');
  return ctx;
}
